import { useEffect, useState, lazy, Suspense, type ComponentType } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { supabase, isSupabaseConfigured, supabaseConfigError } from './lib/supabase';
import { ToastProvider } from './components/ui/Toast';
import { AnimatedBackground } from './components/ui/AnimatedBackground';

// QueryClient é importado SÓ por App.authenticated.tsx (lazy). Login/
// PublicProjectView/Onboarding não usam react-query → vendor-query
// (~27K gzip) NÃO entra no entry. Lighthouse mobile: alavanca pro 85+.

// Auto-reload on stale chunk after deploy
function lazyRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
) {
  return lazy(() =>
    factory().catch((err) => {
      const key = 'chunk_reload';
      if (!sessionStorage.getItem(key)) {
        sessionStorage.setItem(key, '1');
        window.location.reload();
      }
      throw err;
    }),
  );
}

// Authenticated experience lives in a separate chunk: DashboardLayout, Sidebar,
// AICommand, Onboarding, react-router routes e todas as páginas internas só
// carregam quando session existe. Antes vinha tudo no entry e o /login mobile
// pagava ~700K eager.
const AuthenticatedApp = lazyRetry(() => import('./App.authenticated'));

// Rotas públicas (login, /p/:id pública, /onboarding) ficam aqui mesmo pra
// não terem dependência cruzada com o bundle autenticado.
const LoginScreen = lazyRetry(() => import('./pages/LoginScreen').then(m => ({ default: m.LoginScreen as any })));
const PublicProjectView = lazyRetry(() => import('./pages/PublicProjectView').then(m => ({ default: m.PublicProjectView as any })));
const OnboardingPage = lazyRetry(() => import('./pages/OnboardingPage').then(m => ({ default: m.OnboardingPage as any })));

const RouteFallback = () => <div className="min-h-screen bg-[#040410]" />;

function App() {
  const [session, setSession] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const supabaseReady = isSupabaseConfigured;

  useEffect(() => {
    if (!supabaseReady) {
      setLoading(false);
      return;
    }

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setLoading(false);
    }).catch((err) => {
      console.warn("Error getting session:", err);
      // If session fetch fails (e.g. invalid token), we assume logged out
      setSession(null);
      setLoading(false);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
    });

    return () => subscription.unsubscribe();
  }, [supabaseReady]);

  if (!supabaseReady) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center p-6 text-center">
        <div>
          <p className="text-lg font-semibold">Configuracao invalida</p>
          <p className="text-sm text-white/70 mt-2">
            {supabaseConfigError}
          </p>
        </div>
      </div>
    );
  }

  if (loading) return <div className="min-h-screen bg-[#040410]" />;

  return (
    <ToastProvider>
      <AnimatedBackground />
      {session ? (
        <Suspense fallback={<RouteFallback />}>
          <AuthenticatedApp />
        </Suspense>
      ) : (
        <Router>
          <Routes>
            <Route
              path="/login"
              element={
                <Suspense fallback={<RouteFallback />}>
                  <LoginScreen />
                </Suspense>
              }
            />
            <Route
              path="/p/:projectId"
              element={
                <div className="relative z-10">
                  <Suspense fallback={<RouteFallback />}>
                    <PublicProjectView />
                  </Suspense>
                </div>
              }
            />
            <Route
              path="/onboarding"
              element={
                <Suspense fallback={<RouteFallback />}>
                  <OnboardingPage />
                </Suspense>
              }
            />
            <Route
              path="/*"
              element={<Navigate to="/login" />}
            />
          </Routes>
        </Router>
      )}
    </ToastProvider>
  );
}

export default App;
